import { Box, Tab, Tabs, Typography } from '@mui/material'
import { ReactComponent as UsageIcon } from 'assets/icons/UsageIcon.svg'
import { ProgressLoaders, Text } from 'components'
import { color } from 'config/color'
import { getMetricUnit } from 'containers/Usage/components/utils'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { type IPast12MonthsUsageData, type UtilityType } from 'utils/hooks/types'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { DashboardContainer } from './DashboardContainer'
import { UtilityIcon } from './UtilityIcon'
import { formatValue } from 'utils'

interface IPowerCompare {
  past12MonthsUsageData: IPast12MonthsUsageData[]
  isLoading: boolean
}

export const PowerCompare = ({
  past12MonthsUsageData,
  isLoading
}: IPowerCompare): JSX.Element => {
  const { services } = useAuth()
  const startDate = dayjs().subtract(1, 'year').format('MMMM YYYY')
  const endDate = dayjs().format('MMMM YYYY')
  const isMobile = useMobileBreakpoints()
  const [tabValue, setTabValue] = useState<UtilityType | string>('')
  const [totalUsage, setTotalUsage] = useState<number>(0)
  const [monthlyAverage, setMonthlyAverage] = useState<number>(0)

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: UtilityType
  ): void => {
    setTabValue(newValue)
  }

  useEffect(() => {
    setTabValue(services[0])
  }, [])

  useEffect(() => {
    const initPowerCompareData = (): void => {
      const past12Usage = past12MonthsUsageData?.find(
        (usageData: any) => usageData.service === tabValue
      )

      const usageData = past12Usage?.usage?.usageHistory?.[0]?.usageData ?? []
      const totalUsage = usageData?.reduce(
        (acc: number, { value }: any) => acc + parseFloat(value),
        0
      )
      const monthlyAverage = !isNaN(totalUsage) && !isEmpty(usageData)
        ? +(totalUsage / usageData.length).toFixed(2)
        : 0

      setTotalUsage(totalUsage)
      setMonthlyAverage(monthlyAverage)
    }

    initPowerCompareData()
  }, [past12MonthsUsageData, tabValue])

  if (isLoading) {
    return (
      <DashboardContainer minHeight="26vh">
        <ProgressLoaders height="100%" />
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer minHeight="26vh">
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Text color={color.black} fontSize="18px" fontWeight="600">
            Previous 12 months
          </Text>
          <Text color={color.grey[900]} fontSize="12px">
            {`${startDate} - ${endDate}`}
          </Text>
        </Box>
        {services.length > 1 && (
          <Tabs
            sx={{
              bgcolor: color.background[50],
              color: color.grey[700],
              borderRadius: 1,
              '.Mui-selected': {
                bgcolor: color.background[200],
                color: color.grey[900]
              }
            }}
            value={tabValue}
            onChange={handleChange}
            TabIndicatorProps={{ style: { display: 'none' } }}
          >
            {services.map((service) => (
              <Tab
                key={service}
                sx={{ textTransform: 'capitalize' }}
                label={service?.charAt(0) + service?.substring(1).toLowerCase()}
                value={service}
              />
            ))}
          </Tabs>
        )}
      </Box>

      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
        <Box
          display="flex"
          flex={1}
          flexDirection={isMobile ? 'row' : 'column'}
          px={2}
          py={5}
          gap={1}
          border={`1px solid ${color.background[300]}`}
          borderRadius={1}
          alignItems="center"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            bgcolor={color.background[50]}
            p={2}
          >
            <UsageIcon
              fill={services.length > 1 ? color.primary[600] : color.grey[400]}
              height={40}
            />
          </Box>
          <Box display='flex' flexDirection='column' alignItems='center'>
            <Box>
              <Typography
                display="inline"
                color={color.black}
                fontSize={18}
                fontWeight={600}
              >
                {formatValue(String(monthlyAverage), true, false)}
              </Typography>
              <Typography display="inline">&nbsp;</Typography>
              <Typography
                display="inline"
                fontSize={16}
                fontWeight={700}
                color={color.grey[700]}
              >
                {(tabValue !== '') && getMetricUnit(tabValue?.toLocaleLowerCase())}
              </Typography>
            </Box>

            <Typography
              color={color.grey[700]}
              fontSize={14}
            >
              Monthly Average
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          flex={1}
          flexDirection={isMobile ? 'row' : 'column'}
          px={2}
          py={5}
          gap={1}
          border={`1px solid ${color.background[300]}`}
          borderRadius={1}
          alignItems="center"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            bgcolor={color.background[50]}
            p={2}
          >
            <UtilityIcon
              type={tabValue?.toLowerCase()}
              bgColor={color.background[50]}
              iconColor={
                services.length > 1 ? color.primary[600] : color.grey[400]
              }
              fontSize={40}
            />
          </Box>
          <Box display='flex' flexDirection='column' alignItems='center'>
            <Box>
              <Typography
                display="inline"
                color={color.black}
                fontSize={18}
                fontWeight={600}
              >
                {formatValue(String(totalUsage), true, false)}
              </Typography>
              <Typography display="inline">&nbsp;</Typography>
              <Typography
                display="inline"
                fontSize={16}
                fontWeight={700}
                color={color.grey[700]}
              >
                {(tabValue !== '') && getMetricUnit(tabValue?.toLocaleLowerCase())}
              </Typography>
            </Box>

            <Typography
              color={color.grey[700]}
              fontSize={14}
            >
              Total Usage
            </Typography>
          </Box>
        </Box>
      </Box>
    </DashboardContainer>
  )
}
